import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  IconButton,
  Collapse,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Cancel as CancelIcon,
  TaskAlt as TaskAltIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ProjectTile = ({ project, onUpdate }) => {
  const navigate = useNavigate();
  const { user, effectiveRole } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Active/completed projects have real items, comments, disbursements.
  // Server endpoint enforces the auth boundary; the UI also restricts the
  // delete button to admin or the project's own contractor.
  const canDelete =
    user?.role === 'admin' ||
    (effectiveRole === 'contractor' && project.contractor_id === user?.id);

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = (e) => {
    if (e) e.stopPropagation();
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/projects/${project.id}`);
      setDeleteDialogOpen(false);
      onUpdate?.();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  const fetchBudget = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/projects/${project.id}/budget`);
      setBudget(response.data.budget);
    } catch (error) {
      console.error('Failed to fetch budget:', error);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    if (expanded && !budget) {
      fetchBudget();
    }
  }, [expanded, budget, fetchBudget]);

  const getApprovalPercentage = () => {
    if (!budget || budget.total_items === 0) return 0;
    return Math.round((budget.approved_items / budget.total_items) * 100);
  };

  const isFullyApproved = () => {
    return budget && budget.total_items > 0 && budget.approved_items === budget.total_items;
  };

  const approvalPercentage = getApprovalPercentage();

  const getCompletionPercentage = () => {
    if (!budget || budget.approved_items === 0) return 0;
    return Math.min(100, Math.round((budget.completed_items / budget.approved_items) * 100));
  };

  const completionPercentage = getCompletionPercentage();
  const totalDisbursed = budget ? parseFloat(budget.total_disbursed) : 0;
  const totalCost = budget ? parseFloat(budget.total_cost) : 0;

  return (
    <Card
      sx={{
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        borderLeft: `6px solid ${isFullyApproved() ? '#66bb6a' : '#0d47a1'}`,
        backgroundColor: isFullyApproved() ? 'rgba(102, 187, 106, 0.08)' : 'white',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="start">
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              {project.name}
            </Typography>
            {user?.role === 'homeowner' ? (
              project.contractor_first_name ? (
                <Typography variant="body2" color="text.secondary">
                  Contractor: {project.contractor_first_name} {project.contractor_last_name}
                </Typography>
              ) : null
            ) : (
              !project.homeowner_id ? (
                <Chip label="No homeowner assigned" size="small" color="warning" />
              ) : project.homeowner_invite_status === 'pending' ? (
                <Chip label="Homeowner invited — pending" size="small" color="info" />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Homeowner: {project.homeowner_first_name} {project.homeowner_last_name}
                </Typography>
              )
            )}
          </Box>
          <Box display="flex" alignItems="center">
            {canDelete && (
              <Tooltip title="Delete project">
                <IconButton
                  aria-label={`Delete project ${project.name}`}
                  onClick={handleDeleteClick}
                  sx={{ color: 'error.main', mr: 0.5 }}
                  size="small"
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              aria-label={expanded ? 'Collapse project details' : 'Expand project details'}
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: '0.3s',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        {budget && (
          <Box mt={2}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Approval Progress
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {approvalPercentage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={approvalPercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: isFullyApproved() ? '#66bb6a' : '#1976d2',
                },
              }}
            />
            <Box display="flex" gap={2} mt={2} flexWrap="wrap">
              <Chip
                icon={<CheckCircleIcon fontSize="small" />}
                label={`${budget.approved_items} Approved`}
                size="small"
                color="success"
                clickable
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/project/${project.id}?filter=approved`);
                }}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                icon={<ScheduleIcon fontSize="small" />}
                label={`${budget.outstanding_items} Outstanding`}
                size="small"
                color="warning"
                clickable
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/project/${project.id}?filter=outstanding`);
                }}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                icon={<CancelIcon fontSize="small" />}
                label={`${budget.rejected_items} Rejected`}
                size="small"
                color="error"
                clickable
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/project/${project.id}?filter=rejected`);
                }}
                sx={{ cursor: 'pointer' }}
              />
            </Box>

            {budget.completed_items > 0 && (
              <Box mt={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Completion Progress
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {completionPercentage}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={completionPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: completionPercentage === 100 ? '#66bb6a' : '#ff9800',
                    },
                  }}
                />
                <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                  <Chip
                    icon={<TaskAltIcon fontSize="small" />}
                    label={`${budget.completed_items} Completed`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                  {totalDisbursed > 0 && (
                    <Chip
                      icon={<MoneyIcon fontSize="small" />}
                      label={`$${totalDisbursed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Disbursed`}
                      size="small"
                      sx={{ backgroundColor: 'rgba(102, 187, 106, 0.15)', color: '#2e7d32' }}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box mt={3}>
            {loading ? (
              <LinearProgress />
            ) : budget ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box
                    p={2}
                    sx={{
                      backgroundColor: 'rgba(13, 71, 161, 0.1)',
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    <MoneyIcon color="success" />
                    <Typography variant="body2" color="text.secondary">
                      Total Budget
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ${parseFloat(budget.total_cost).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box
                    p={2}
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Labor
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ${parseFloat(budget.total_labor).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box
                    p={2}
                    sx={{
                      backgroundColor: 'rgba(66, 165, 245, 0.1)',
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Materials
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ${parseFloat(budget.total_materials).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
                {(totalDisbursed > 0 || budget.completed_items > 0) && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Box
                        p={2}
                        sx={{
                          backgroundColor: 'rgba(102, 187, 106, 0.1)',
                          borderRadius: 2,
                          textAlign: 'center',
                        }}
                      >
                        <MoneyIcon sx={{ color: '#2e7d32' }} />
                        <Typography variant="body2" color="text.secondary">
                          Disbursed
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                          ${totalDisbursed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box
                        p={2}
                        sx={{
                          backgroundColor: 'rgba(255, 152, 0, 0.1)',
                          borderRadius: 2,
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Remaining
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#e65100' }}>
                          ${(totalCost - totalDisbursed).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/project/${project.id}`);
                    }}
                  >
                    View Full Project
                  </Button>
                </Grid>
              </Grid>
            ) : null}
          </Box>
        </Collapse>
      </CardContent>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete project "{project.name}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently soft-deletes the project and everything attached: rooms, items,
            comments, photo attachments, and disbursement history. The action can be undone
            only by digging into the database directly. Make sure you really want this.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit" disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default React.memo(ProjectTile);
